import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Eye, EyeOff, CheckCircle2, Circle, Instagram, UserPlus, HardDrive, Send, RefreshCw } from "lucide-react";
import { priceLabel, formatDate } from "../lib/constants";
import AdminAIHelper from "../components/AdminAIHelper";

const NEWS_CATEGORIES = ["Geral", "Produtos", "Cooperativa", "Movelaria", "Comunidade"];
const PRODUCT_CATEGORIES = [
  { key: "oleos", label: "Óleos Vegetais & Mel" },
  { key: "artesanato", label: "Artesanato" },
  { key: "movelaria", label: "Movelaria Anambé" },
];

export default function AdminDashboard() {
  const { user, logout, loading } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!loading && !user) nav("/admin/login");
  }, [user, loading, nav]);

  if (loading || !user) return <div className="container-page py-20 text-forest-900/60">Carregando...</div>;

  return (
    <div data-testid="admin-dashboard" className="py-14">
      <div className="container-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <div className="eyebrow mb-2">Painel administrativo</div>
            <h1 className="font-display text-4xl text-forest-900">Olá, {user.name || user.email}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="btn-outline !py-2.5 !px-4" data-testid="admin-view-site">Ver site</Link>
            <button onClick={logout} className="btn-primary !py-2.5 !px-4" data-testid="admin-logout">
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>

        <Tabs defaultValue="news" className="w-full">
          <TabsList className="bg-cream-200 border border-cream-400 flex flex-wrap h-auto">
            <TabsTrigger value="news" data-testid="tab-news">Notícias</TabsTrigger>
            <TabsTrigger value="products" data-testid="tab-products">Produtos</TabsTrigger>
            <TabsTrigger value="instagram" data-testid="tab-instagram">Instagram</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Mensagens</TabsTrigger>
            <TabsTrigger value="secretaries" data-testid="tab-secretaries">Secretários</TabsTrigger>
            <TabsTrigger value="ai" data-testid="tab-ai">Assistente IA</TabsTrigger>
            <TabsTrigger value="storage" data-testid="tab-storage">Manutenção</TabsTrigger>
          </TabsList>

          <TabsContent value="news"><NewsManager /></TabsContent>
          <TabsContent value="products"><ProductsManager /></TabsContent>
          <TabsContent value="instagram"><InstagramManager /></TabsContent>
          <TabsContent value="contacts"><ContactsManager /></TabsContent>
          <TabsContent value="secretaries"><SecretariesManager /></TabsContent>
          <TabsContent value="ai"><AdminAIHelper /></TabsContent>
          <TabsContent value="storage"><StorageManager /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

/* --------------- News --------------- */
function NewsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/news").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Excluir esta notícia?")) return;
    try { await api.delete(`/admin/news/${id}`); toast.success("Notícia removida."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-forest-900">Notícias cadastradas ({items.length})</h2>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-news-button" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
          <Plus className="w-4 h-4 mr-2" /> Nova notícia
        </Button>
      </div>
      <div className="bg-white border border-cream-400">
        <div className="hidden md:grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-[0.18em] text-forest-900/60 border-b border-cream-400">
          <div className="col-span-5">Título</div>
          <div className="col-span-2">Categoria</div>
          <div className="col-span-2">Data</div>
          <div className="col-span-1">Publicado</div>
          <div className="col-span-2 text-right">Ações</div>
        </div>
        {items.map((n) => (
          <div key={n.id} data-testid={`admin-news-row-${n.id}`} className="grid grid-cols-1 md:grid-cols-12 items-center px-5 py-4 border-b last:border-0 border-cream-400 gap-2">
            <div className="col-span-5 flex items-center gap-3">
              <img src={n.image_url} alt="" className="w-14 h-14 object-cover" />
              <div className="font-medium text-forest-900">{n.title}</div>
            </div>
            <div className="col-span-2 text-sm text-forest-900/70">{n.category}</div>
            <div className="col-span-2 text-sm text-forest-900/70">{formatDate(n.created_at)}</div>
            <div className="col-span-1">{n.published ? <Eye className="w-4 h-4 text-forest-700" /> : <EyeOff className="w-4 h-4 text-terracotta-500" />}</div>
            <div className="col-span-2 flex md:justify-end gap-2">
              <button onClick={() => { setEditing(n); setOpen(true); }} data-testid={`edit-news-${n.id}`} className="p-2 text-forest-700 hover:bg-cream-200"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => onDelete(n.id)} data-testid={`delete-news-${n.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="px-5 py-10 text-forest-900/60">Nenhuma notícia ainda.</div>}
      </div>

      <NewsDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function NewsDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ title: "", excerpt: "", content: "", image_url: "", category: "Geral", published: true, gallery: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [newVideoUrl, setNewVideoUrl] = useState("");

  useEffect(() => {
    if (editing) setForm({
      title: editing.title, excerpt: editing.excerpt, content: editing.content,
      image_url: editing.image_url, category: editing.category, published: editing.published,
      gallery: editing.gallery || [],
    });
    else setForm({ title: "", excerpt: "", content: "", image_url: "", category: "Geral", published: true, gallery: [] });
  }, [editing, open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Auto-deriva o resumo a partir do início do conteúdo (mantém compatibilidade com cards/WhatsApp)
      const autoExcerpt = (form.content || "").trim().slice(0, 220).replace(/\s+\S*$/, "") + ((form.content || "").length > 220 ? "..." : "");
      const payload = { ...form, excerpt: autoExcerpt };
      if (editing) { await api.put(`/admin/news/${editing.id}`, payload); toast.success("Notícia atualizada."); }
      else { await api.post("/admin/news", payload); toast.success("Notícia criada."); }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const newItems = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f, f.name);
        const { data } = await api.post("/admin/news/upload-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newItems.push({ type: data.type, url: data.url, caption: "" });
      }
      setForm((s) => ({ ...s, gallery: [...(s.gallery || []), ...newItems] }));
      toast.success(`${newItems.length} arquivo(s) enviado(s)!`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleCoverUpload = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Envie apenas imagens (JPG, PNG, WebP)."); return; }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const { data } = await api.post("/admin/news/upload-media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_url: data.url }));
      toast.success("Imagem de capa enviada!");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingCover(false); e.target.value = ""; }
  };

  const detectVideoType = (url) => {
    if (/youtu\.?be/.test(url)) return "youtube";
    if (/instagram\.com/.test(url)) return "instagram";
    return null;
  };

  const addVideoUrl = () => {
    const u = newVideoUrl.trim();
    if (!u) return;
    const type = detectVideoType(u);
    if (!type) { toast.error("Cole um link do YouTube ou Instagram."); return; }
    setForm((s) => ({ ...s, gallery: [...(s.gallery || []), { type, url: u, caption: "" }] }));
    setNewVideoUrl("");
    toast.success("Vídeo adicionado à galeria!");
  };

  const removeGallery = (idx) => setForm((s) => ({ ...s, gallery: s.gallery.filter((_, i) => i !== idx) }));
  const updateCaption = (idx, caption) => setForm((s) => ({ ...s, gallery: s.gallery.map((g, i) => i === idx ? { ...g, caption } : g) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Editar notícia" : "Nova notícia"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div><Label>Título</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="news-form-title" /></div>
          <div><Label>Conteúdo</Label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} required rows={8} data-testid="news-form-content" placeholder="Escreva o conteúdo completo da notícia. O resumo dos cards e do compartilhamento é gerado automaticamente." /></div>

          {/* Imagem de capa — upload direto */}
          <div className="border border-cream-400 p-4 bg-cream-100">
            <Label className="text-base">Imagem de capa *</Label>
            <p className="text-xs text-forest-900/65 mt-1 mb-3">Envie a imagem principal da notícia (JPG/PNG/WebP · máx 5MB)</p>
            {form.image_url ? (
              <div className="flex items-start gap-4">
                <img
                  src={form.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url}
                  alt="capa" className="w-40 h-28 object-cover border border-cream-400"
                  data-testid="news-cover-preview"
                />
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-forest-700 text-forest-900 hover:bg-cream-200 text-xs">
                    {uploadingCover ? "Enviando..." : "Trocar imagem"}
                    <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" disabled={uploadingCover} data-testid="news-cover-upload" />
                  </label>
                  <button type="button" onClick={() => setForm((s) => ({ ...s, image_url: "" }))} className="text-xs text-terracotta-500 hover:text-terracotta-700 text-left" data-testid="news-cover-remove">
                    Remover imagem
                  </button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 border border-dashed border-forest-700 text-forest-900 hover:bg-cream-200 text-sm">
                {uploadingCover ? "Enviando..." : "+ Adicionar imagem de capa"}
                <input type="file" accept="image/*" onChange={handleCoverUpload} className="hidden" disabled={uploadingCover} data-testid="news-cover-upload" />
              </label>
            )}
          </div>

          {/* Galeria de mídia */}
          <div className="border border-cream-400 p-4 bg-cream-100">
            <div className="flex items-center justify-between mb-3">
              <div>
                <Label className="text-base">Galeria de imagens e vídeos</Label>
                <p className="text-xs text-forest-900/65 mt-1">Imagens (5MB), vídeos MP4 (50MB) ou link YouTube/Instagram</p>
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 bg-forest-700 text-cream-100 hover:bg-forest-900 text-xs">
                  {uploading ? "Enviando..." : "+ Adicionar imagem/vídeo"}
                  <input type="file" multiple accept="image/*,video/mp4,video/quicktime,video/webm" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="news-gallery-upload" />
                </label>
              </div>
            </div>
            <div className="flex gap-2 mb-3">
              <Input
                value={newVideoUrl}
                onChange={(e) => setNewVideoUrl(e.target.value)}
                placeholder="Cole link do YouTube ou Instagram aqui..."
                data-testid="news-gallery-url-input"
              />
              <Button type="button" onClick={addVideoUrl} className="rounded-none bg-copaiba-500 text-forest-900 hover:bg-copaiba-300" data-testid="news-gallery-add-url">Adicionar link</Button>
            </div>
            {form.gallery.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {form.gallery.map((item, idx) => (
                  <div key={idx} className="relative bg-white border border-cream-400 p-2" data-testid={`gallery-item-${idx}`}>
                    {item.type === "image" ? (
                      <img
                        src={item.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${item.url}` : item.url}
                        alt="" className="w-full h-24 object-cover" />
                    ) : item.type === "video_upload" ? (
                      <video src={item.url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${item.url}` : item.url} className="w-full h-24 object-cover bg-black" />
                    ) : (
                      <div className="w-full h-24 bg-cream-200 flex items-center justify-center text-xs text-forest-900/65 px-2 text-center capitalize">
                        🎥 {item.type}<br /><span className="text-[10px] truncate w-full inline-block">{item.url}</span>
                      </div>
                    )}
                    <Input
                      value={item.caption || ""}
                      onChange={(e) => updateCaption(idx, e.target.value)}
                      placeholder="Legenda (opcional)"
                      className="mt-2 h-8 text-xs"
                      data-testid={`gallery-caption-${idx}`}
                    />
                    <button type="button" onClick={() => removeGallery(idx)} data-testid={`gallery-remove-${idx}`} className="absolute top-1 right-1 bg-terracotta-500 text-cream-100 w-6 h-6 flex items-center justify-center hover:bg-terracotta-700">×</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-xs text-forest-900/55 py-3 text-center">Nenhuma mídia adicional. Use os botões acima para enriquecer a notícia.</div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="news-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{NEWS_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-3">
              <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="news-form-published" />
              <Label>Publicada</Label>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="news-form-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* --------------- Products --------------- */
function ProductsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Excluir este produto?")) return;
    try { await api.delete(`/admin/products/${id}`); toast.success("Produto removido."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-forest-900">Produtos cadastrados ({items.length})</h2>
        <Button onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-product-button" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
          <Plus className="w-4 h-4 mr-2" /> Novo produto
        </Button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <div key={p.id} className="bg-white border border-cream-400 p-5 flex gap-4" data-testid={`admin-product-row-${p.id}`}>
            <img src={p.image_url && p.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url} alt="" className="w-20 h-24 object-cover shrink-0" />
            <div className="flex-1">
              <div className="eyebrow mb-1">{p.category}</div>
              <div className="font-medium text-forest-900 leading-tight">{p.name}</div>
              <div className="text-copaiba-700 font-medium mt-2">{priceLabel(p.price)}</div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-product-${p.id}`} className="p-2 text-forest-700 hover:bg-cream-200"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => onDelete(p.id)} data-testid={`delete-product-${p.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-forest-900/60">Nenhum produto ainda.</div>}
      </div>

      <ProductDialog open={open} onOpenChange={setOpen} editing={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function ProductDialog({ open, onOpenChange, editing, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", price: 0, image_url: "", category: "copaiba", in_stock: true, highlights: "" });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (editing) setForm({
      name: editing.name, description: editing.description, price: editing.price,
      image_url: editing.image_url, category: editing.category, in_stock: editing.in_stock,
      highlights: (editing.highlights || []).join("\n"),
    });
    else setForm({ name: "", description: "", price: 0, image_url: "", category: "oleos", in_stock: true, highlights: "" });
  }, [editing, open]);

  const handleImageUpload = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Envie apenas imagens (JPG, PNG, WebP)."); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const { data } = await api.post("/admin/products/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_url: data.url }));
      toast.success("Imagem do produto enviada!");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploadingImage(false); e.target.value = ""; }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_url) { toast.error("Envie a imagem do produto antes de salvar."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        price: Number(form.price),
        image_url: form.image_url,
        category: form.category,
        in_stock: form.in_stock,
        highlights: form.highlights.split("\n").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) { await api.put(`/admin/products/${editing.id}`, payload); toast.success("Produto atualizado."); }
      else { await api.post("/admin/products", payload); toast.success("Produto criado."); }
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const previewSrc = form.image_url
    ? (form.image_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${form.image_url}` : form.image_url)
    : "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{editing ? "Editar produto" : "Novo produto"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="product-form-name" /></div>
          <div><Label>Descrição</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={4} data-testid="product-form-description" /></div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Preço (R$) — deixe 0 para &quot;Sob consulta&quot;</Label>
              <Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-form-price" />
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="product-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{PRODUCT_CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Imagem do produto — upload direto */}
          <div className="border border-cream-400 p-4 bg-cream-100">
            <Label className="text-base">Imagem do produto *</Label>
            <p className="text-xs text-forest-900/65 mt-1 mb-3">Envie a foto principal (JPG/PNG/WebP · máx 5MB)</p>
            {form.image_url ? (
              <div className="flex items-start gap-4">
                <img
                  src={previewSrc}
                  alt="Preview do produto"
                  className="w-40 h-40 object-cover border border-cream-400"
                  data-testid="product-image-preview"
                />
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 border border-forest-700 text-forest-900 hover:bg-cream-200 text-xs">
                    {uploadingImage ? "Enviando..." : "Trocar imagem"}
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} data-testid="product-image-upload" />
                  </label>
                  <button type="button" onClick={() => setForm((s) => ({ ...s, image_url: "" }))} className="text-xs text-terracotta-500 hover:text-terracotta-700 text-left" data-testid="product-image-remove">
                    Remover imagem
                  </button>
                </div>
              </div>
            ) : (
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 border border-dashed border-forest-700 text-forest-900 hover:bg-cream-200 text-sm">
                {uploadingImage ? "Enviando..." : "+ Adicionar imagem do produto"}
                <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} data-testid="product-image-upload" />
              </label>
            )}
          </div>

          <div><Label>Diferenciais (um por linha)</Label><Textarea value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} rows={3} data-testid="product-form-highlights" /></div>
          <div className="flex items-center gap-3">
            <Switch checked={form.in_stock} onCheckedChange={(v) => setForm({ ...form, in_stock: v })} data-testid="product-form-stock" />
            <Label>Em estoque</Label>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving || uploadingImage} data-testid="product-form-submit" className="bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* --------------- Contacts --------------- */
function ContactsManager() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/contacts").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try { await api.post(`/admin/contacts/${id}/read`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Excluir esta mensagem?")) return;
    try { await api.delete(`/admin/contacts/${id}`); toast.success("Mensagem removida."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8">
      <h2 className="font-display text-2xl text-forest-900 mb-6">Mensagens de contato ({items.length})</h2>
      <div className="space-y-4">
        {items.map((c) => (
          <div key={c.id} data-testid={`admin-contact-${c.id}`} className={`bg-white border p-5 ${c.read ? "border-cream-400" : "border-copaiba-500"}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  {c.read ? <CheckCircle2 className="w-4 h-4 text-forest-700" /> : <Circle className="w-4 h-4 text-copaiba-500" />}
                  <div className="font-medium text-forest-900">{c.name} &middot; <span className="text-forest-900/70">{c.email}</span></div>
                </div>
                <div className="eyebrow mt-1">{formatDate(c.created_at)} &middot; {c.subject}</div>
                <p className="text-forest-900/85 mt-3 whitespace-pre-wrap">{c.message}</p>
                {c.phone && <div className="text-sm text-forest-900/70 mt-2">Telefone: {c.phone}</div>}
              </div>
              <div className="flex gap-2 shrink-0">
                {!c.read && <button onClick={() => markRead(c.id)} data-testid={`mark-read-${c.id}`} className="p-2 text-forest-700 hover:bg-cream-200"><CheckCircle2 className="w-4 h-4" /></button>}
                <button onClick={() => del(c.id)} data-testid={`delete-contact-${c.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-forest-900/60">Nenhuma mensagem recebida ainda.</div>}
      </div>
    </div>
  );
}


/* --------------- Instagram --------------- */
function InstagramManager() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/instagram").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setSaving(true);
    try {
      await api.post("/admin/instagram", { url: url.trim(), caption: caption.trim() });
      toast.success("Publicação adicionada!");
      setUrl(""); setCaption("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remover esta publicação?")) return;
    try { await api.delete(`/admin/instagram/${id}`); toast.success("Publicação removida."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8 grid lg:grid-cols-3 gap-6">
      <aside className="lg:col-span-1 space-y-4">
        <div className="bg-forest-700 text-cream-100 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Instagram className="w-5 h-5 text-copaiba-300" />
            <div className="font-display text-xl">Publicações do Instagram</div>
          </div>
          <p className="text-cream-100/80 text-sm leading-relaxed">
            Cole o link de um post, reel ou IGTV do <strong>@coomflona.oficial</strong> e ele aparecerá automaticamente
            na página <strong>/instagram</strong> do site, com o embed oficial do Instagram.
          </p>
        </div>

        <form onSubmit={add} className="bg-white border border-cream-400 p-5 space-y-3" data-testid="instagram-add-form">
          <div>
            <Label>Link do post</Label>
            <Input
              type="url"
              required
              placeholder="https://www.instagram.com/p/XXXXXXXXX/"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="instagram-url-input"
            />
          </div>
          <div>
            <Label>Legenda no site (opcional)</Label>
            <Textarea
              rows={2}
              placeholder="Texto curto que aparece abaixo do post no site"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="instagram-caption-input"
            />
          </div>
          <Button
            type="submit"
            disabled={saving}
            data-testid="instagram-add-submit"
            className="w-full bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none"
          >
            <Plus className="w-4 h-4 mr-2" /> {saving ? "Adicionando..." : "Adicionar publicação"}
          </Button>
        </form>
      </aside>

      <section className="lg:col-span-2">
        <h2 className="font-display text-2xl text-forest-900 mb-5">Publicações no site ({items.length})</h2>
        {items.length === 0 ? (
          <div className="bg-cream-200 border border-cream-400 p-8 text-forest-900/70 text-center">
            Nenhuma publicação adicionada ainda. Cole um link ao lado para começar.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {items.map((p) => (
              <div key={p.id} data-testid={`admin-instagram-${p.id}`} className="bg-white border border-cream-400 overflow-hidden">
                <div className="aspect-square">
                  <iframe
                    src={`https://www.instagram.com/p/${p.shortcode}/embed/`}
                    title={p.shortcode}
                    loading="lazy"
                    scrolling="no"
                    className="w-full h-full border-0"
                  />
                </div>
                {p.caption && <div className="p-3 text-sm text-forest-900/80 border-t border-cream-400">{p.caption}</div>}
                <div className="border-t border-cream-400 px-3 py-2 flex items-center justify-between text-xs">
                  <a href={p.url} target="_blank" rel="noreferrer" className="text-forest-700 hover:text-copaiba-700 truncate max-w-[200px]">
                    {p.shortcode}
                  </a>
                  <button
                    onClick={() => del(p.id)}
                    data-testid={`delete-instagram-${p.id}`}
                    className="p-1.5 text-terracotta-500 hover:bg-cream-200"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/* --------------- Secretários --------------- */
function SecretariesManager() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/secretaries").then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) return toast.error("A senha precisa ter pelo menos 6 caracteres.");
    setSaving(true);
    try {
      await api.post("/admin/secretaries", form);
      toast.success("Secretário(a) cadastrado(a)!");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remover este acesso? O secretário não poderá mais entrar.")) return;
    try { await api.delete(`/admin/secretaries/${id}`); toast.success("Acesso removido."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-8 grid lg:grid-cols-3 gap-6">
      <aside className="lg:col-span-1 space-y-4">
        <div className="bg-forest-700 text-cream-100 p-5">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="w-5 h-5 text-copaiba-300" />
            <div className="font-display text-xl">Acesso da equipe</div>
          </div>
          <p className="text-cream-100/80 text-sm leading-relaxed">
            Cadastre secretários para acessarem a <strong>Secretaria Virtual</strong> em /secretaria/painel. Cada
            secretário tem login próprio e não vê este painel administrativo.
          </p>
        </div>

        <form onSubmit={add} className="bg-white border border-cream-400 p-5 space-y-3" data-testid="secretary-add-form">
          <div>
            <Label>Nome</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="secretary-name-input" />
          </div>
          <div>
            <Label>E-mail</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="secretary-email-input" />
          </div>
          <div>
            <Label>Senha (mín. 6 caracteres)</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="secretary-password-input" />
          </div>
          <Button type="submit" disabled={saving} data-testid="secretary-add-submit" className="w-full bg-forest-700 hover:bg-forest-900 text-cream-100 rounded-none">
            <Plus className="w-4 h-4 mr-2" /> {saving ? "Cadastrando..." : "Cadastrar secretário"}
          </Button>
        </form>
      </aside>

      <section className="lg:col-span-2">
        <h2 className="font-display text-2xl text-forest-900 mb-5">Secretários cadastrados ({items.length})</h2>
        <div className="bg-white border border-cream-400">
          {items.length === 0 ? (
            <div className="p-8 text-center text-forest-900/65">Nenhum secretário cadastrado ainda.</div>
          ) : items.map((s) => (
            <div key={s.id} data-testid={`secretary-row-${s.id}`} className="flex items-center justify-between px-5 py-4 border-b last:border-0 border-cream-400">
              <div>
                <div className="font-medium text-forest-900">{s.name}</div>
                <div className="text-sm text-forest-900/70">{s.email}</div>
                <div className="text-xs text-forest-900/55 mt-1">Cadastrado em {formatDate(s.created_at)}</div>
              </div>
              <button onClick={() => del(s.id)} data-testid={`delete-secretary-${s.id}`} className="p-2 text-terracotta-500 hover:bg-cream-200"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}



/* --------------- Storage / Manutenção --------------- */
function StorageManager() {
  const [status, setStatus] = useState(null);
  const [runningBf, setRunningBf] = useState(false);
  const [health, setHealth] = useState(null);
  const [loadingHealth, setLoadingHealth] = useState(false);

  const loadStatus = () => api.get("/admin/storage-backfill-status")
    .then((r) => setStatus(r.data))
    .catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { loadStatus(); }, []);

  const runBackfill = async () => {
    setRunningBf(true);
    try {
      const { data } = await api.post("/admin/storage-backfill-all");
      if (data.already_running) toast.info("Backfill já em andamento — aguarde.");
      else toast.success("Migração iniciada em segundo plano.");
      loadStatus();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunningBf(false); }
  };

  const loadHealth = async () => {
    setLoadingHealth(true);
    try {
      const { data } = await api.get("/admin/files-health/applications");
      setHealth(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingHealth(false); }
  };

  const reRequestWA = (it) => {
    const link = `${window.location.origin}/vagas`;
    const msg = `Olá ${it.name}! Sou da COOMFLONA. Infelizmente perdemos o currículo que você enviou para a vaga *${it.job_title}* durante uma manutenção do sistema. Você poderia por favor reenviar o currículo em PDF acessando ${link} e refazendo a inscrição? Pedimos desculpas pelo transtorno.\n— Secretaria Virtual`;
    window.open(`https://wa.me/${(it.phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const st = status?.state;
  const s = status?.storage;
  const bfRunning = st?.running;

  return (
    <div className="mt-6 space-y-8">
      <section className="bg-white border border-cream-400 p-5">
        <h3 className="font-display text-xl text-forest-900 flex items-center gap-2 mb-2">
          <HardDrive className="w-5 h-5" /> Migração de arquivos para Object Storage persistente
        </h3>
        <p className="text-sm text-forest-900/70 mb-4">
          A cada redeploy da produção, arquivos guardados apenas no disco local são apagados. Este botão
          envia TODOS os arquivos que ainda estão no disco (currículos das candidaturas, anexos do Setor Social,
          documentos dos brigadistas do PSS) para o Object Storage — assim eles sobrevivem a futuros redeploys.
          A operação roda em segundo plano e é idempotente.
        </p>

        <div className="bg-cream-100 border border-cream-400 p-3 mb-4 text-xs" data-testid="storage-state">
          <div>Storage inicializado: <b>{s?.initialized ? "✅ sim" : "❌ não"}</b></div>
          {s?.last_init_error && <div className="text-terracotta-500">Último erro: {s.last_init_error}</div>}
          <div className="mt-1">Migração: <b>{bfRunning ? "🔄 em andamento" : (st?.finished_at ? "✅ concluída" : "— nunca rodou nesta sessão")}</b></div>
          {st?.started_at && <div>Início: {new Date(st.started_at).toLocaleString("pt-BR")}</div>}
          {st?.finished_at && <div>Fim: {new Date(st.finished_at).toLocaleString("pt-BR")}</div>}
          {st?.result && (
            <ul className="mt-2 list-disc ml-5">
              {["pss", "files", "social"].map((k) => st.result[k] && (
                <li key={k}><b>{k}</b>: verificados={st.result[k].scanned}, enviados={st.result[k].uploaded}, já-existiam={st.result[k].already}, falhas={st.result[k].failed}</li>
              ))}
            </ul>
          )}
          {st?.error && <div className="text-terracotta-500 mt-2">Erro: {st.error}</div>}
        </div>

        <div className="flex gap-2 flex-wrap">
          <button onClick={runBackfill} disabled={runningBf || bfRunning} data-testid="run-backfill"
            className="btn-primary !py-2.5 !px-4 disabled:opacity-60">
            <HardDrive className="w-4 h-4 mr-2" /> {runningBf || bfRunning ? "Migrando..." : "Rodar migração agora"}
          </button>
          <button onClick={loadStatus} data-testid="reload-storage-status"
            className="btn-outline !py-2.5 !px-4">
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar status
          </button>
        </div>
      </section>

      <section className="bg-white border border-cream-400 p-5">
        <h3 className="font-display text-xl text-forest-900 flex items-center gap-2 mb-2">
          <Eye className="w-5 h-5" /> Saúde dos currículos das candidaturas
        </h3>
        <p className="text-sm text-forest-900/70 mb-4">
          Lista todas as candidaturas com CV e mostra se o arquivo está acessível. Para os que estiverem
          perdidos, você pode enviar uma mensagem no WhatsApp pedindo o reenvio.
        </p>

        <button onClick={loadHealth} data-testid="load-health" disabled={loadingHealth}
          className="btn-primary !py-2.5 !px-4 mb-4 disabled:opacity-60">
          {loadingHealth ? "Verificando..." : "Verificar saúde"}
        </button>

        {health && (
          <div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="bg-cream-100 border border-cream-400 p-3 text-center">
                <div className="font-display text-3xl text-forest-900">{health.total}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-forest-900/65">Total</div>
              </div>
              <div className="bg-forest-700 text-cream-100 p-3 text-center">
                <div className="font-display text-3xl text-copaiba-300">{health.available}</div>
                <div className="text-xs uppercase tracking-[0.18em]">Disponíveis</div>
              </div>
              <div className="bg-terracotta-500 text-cream-100 p-3 text-center">
                <div className="font-display text-3xl">{health.missing}</div>
                <div className="text-xs uppercase tracking-[0.18em]">Perdidos</div>
              </div>
            </div>

            <div className="overflow-x-auto" data-testid="health-table">
              <table className="w-full text-sm">
                <thead className="bg-cream-200">
                  <tr>
                    <th className="px-3 py-2 text-left">Candidato</th>
                    <th className="px-3 py-2 text-left">Vaga</th>
                    <th className="px-3 py-2 text-center">Storage</th>
                    <th className="px-3 py-2 text-center">Disco</th>
                    <th className="px-3 py-2 text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {health.items.map((it) => (
                    <tr key={it.id} className="border-t border-cream-400" data-testid={`health-row-${it.id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-forest-900">{it.name}</div>
                        <div className="text-xs text-forest-900/60">{it.phone || it.email}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">{it.job_title}</td>
                      <td className="px-3 py-2 text-center">{it.in_storage ? "✅" : "❌"}</td>
                      <td className="px-3 py-2 text-center">{it.on_disk ? "✅" : "❌"}</td>
                      <td className="px-3 py-2 text-center">
                        {it.available ? (
                          <span className="text-xs text-forest-700">disponível</span>
                        ) : (
                          <button onClick={() => reRequestWA(it)} data-testid={`re-request-${it.id}`}
                            className="inline-flex items-center gap-1 text-xs px-2 py-1 bg-[#25D366] text-white hover:brightness-95">
                            <Send className="w-3 h-3" /> Pedir reenvio
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
